import _ from 'lodash';
import clsx from 'clsx';
import React from 'react';
import MuiSlider, { Mark, ValueLabelProps } from '@material-ui/core/Slider';

import { makeStyles } from '../styles';
import { Colors } from 'react-scale/components/legacy-css/global_css';

interface SliderProps {
  className?: string;
  minVal?: number;
  maxVal?: number;
  step?: number | null;
  vertical?: boolean;
  onMouseDown?: () => void;
  onMouseUp?: () => void;
  disabled?: boolean;
  marks?: boolean | Mark[];
  valueLabelDisplay?: 'on' | 'off' | 'auto';
  valueLabelFormat?: (value: number) => React.ReactNode;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  iconMargin?: number;
  color?: string;
  track?: 'normal' | false | 'inverted';
  useRemoBlue?: boolean;
}

type Controlled<T = number | [number, number]> = {
  position?: T;
};

type Uncontrolled<T = number | [number, number]> = {
  defaultValue?: T;
};

type MaybeControlled<T> = Controlled<T> | Uncontrolled<T>;

type ValueProps = MaybeControlled<number> & {
  onAdjust?: (position: number, eventName?: string) => void;
  onChangeCommitted?: (event: React.ChangeEvent<{}>, value: number) => void;
};

type RangeProps = MaybeControlled<[number, number]> & {
  onAdjust?: (position: [number, number], eventName?: string) => void;
  onChangeCommitted?: (event: React.ChangeEvent<{}>, value: [number, number]) => void;
};

export const Slider: React.FC<SliderProps & (ValueProps | RangeProps)> = React.memo<
  SliderProps & (ValueProps | RangeProps)
>(function Slider(props) {
  const classes = useSliderStyles(props);
  const onChange = (event: React.ChangeEvent<{}>, value: number | number[]) => {
    if (props.onAdjust) {
      props.onAdjust(value as any, 'change');
    }
  };
  const min = props.minVal ?? 0;
  const max = props.maxVal ?? 1;
  /** When using marks, setting `step` to null forces value to always equal a mark */
  const stepSize = _.isNull(props.step) ? props.step : props.step || (max - min) / 100;

  return (
    <div
      className={clsx(props.className, {
        [classes.horizontal]: !props.vertical,
        [classes.vertical]: !!props.vertical,
      })}
    >
      {props.startIcon && (
        <div className={clsx(classes.start, classes.icon)}>{props.startIcon}</div>
      )}
      <MuiSlider
        classes={{
          root: clsx(classes.root, {
            [classes.white]: props.color === 'white',
            [classes.black]: props.color === 'black',
            [classes.verticalRoot]: !!props.vertical,
          }),
          rail: classes.rail,
          track: classes.track,
          thumb: clsx(classes.thumb, {
            [classes.thumbHidden]: (props as Controlled).position === undefined,
          }),
          valueLabel: classes.valueLabel,
          disabled: classes.disabled,
          mark: clsx(classes.mark, { [classes.whiteMark]: props.color === 'white' }),
          markLabel: clsx(classes.markLabel, { [classes.whiteMarkLabel]: props.color === 'white' }),
        }}
        orientation={props.vertical ? 'vertical' : 'horizontal'}
        defaultValue={(props as Uncontrolled).defaultValue}
        value={(props as Controlled).position}
        step={stepSize}
        min={min}
        max={max}
        disabled={props.disabled}
        marks={props.marks}
        track={props.track}
        valueLabelDisplay={props.valueLabelDisplay}
        valueLabelFormat={props.valueLabelFormat}
        ValueLabelComponent={ValueLabelComponent}
        onMouseDown={props.onMouseDown}
        onChange={props.onAdjust ? onChange : undefined}
        onChangeCommitted={props.onChangeCommitted as any}
      />
      {props.endIcon && <div className={clsx(classes.end, classes.icon)}>{props.endIcon}</div>}
    </div>
  );
});

const useSliderStyles = makeStyles<SliderProps>(theme => ({
  horizontal: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  vertical: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  root: {
    height: 4,
    padding: '0',
    minWidth: theme.spacing(5),
  },
  verticalRoot: {
    padding: `0 ${theme.spacing(4)}px !important`,
    minWidth: 'auto',
  },
  rail: {
    height: 4,
    borderRadius: 2,
    background: Colors.RemoGray50,
    width: '101%', // otherwise the border radius ends before the last tick
  },
  track: props => ({
    top: -1,
    height: 6,
    borderRadius: 2,
    background: props.useRemoBlue ? theme.colors.blue : theme.colors.periwinkle100,
  }),
  thumb: props => ({
    marginTop: -6,
    marginLeft: -4,
    background: props.useRemoBlue ? theme.colors.blue : theme.colors.periwinkle100,
    width: 16,
    height: 16,
  }),
  thumbHidden: {
    display: 'none',
  },
  disabled: {
    '& > $thumb': {
      background: theme.colors.periwinkle50,
      marginTop: -6,
      marginLeft: -4,
      width: 16,
      height: 16,
    },
    '& > $track': {
      background: theme.colors.periwinkle50,
    },
    '& > $mark': {
      border: `2px solid ${theme.colors.periwinkle50}`,
      background: theme.colors.periwinkle50,
    },
  },
  white: {
    '& > $rail': {
      background: Colors.RemoGray50,
    },
    '& > $thumb': {
      background: Colors.RemoGray20,
    },
    '& > $track': {
      background: Colors.RemoGray10,
    },
  },
  black: {
    '& > $rail': {
      background: 'black',
    },
    '& > $thumb': {
      background: 'black',
    },
    '& > $track': {
      background: 'black',
    },
  },
  mark: {
    border: `2px solid ${theme.colors.periwinkle100}`,
    background: theme.colors.periwinkle100,
    top: -3,
    width: 0.02,
    height: 10,
    opacity: 100,
    borderRadius: 2,
  },
  whiteMark: {
    border: '2px solid #000',
  },
  markLabel: {
    fontSize: '11px',
    lineHeight: '16px',
    letterSpacing: '0.1px',
    minWidth: 12,
    textAlign: 'center',
    marginLeft: 2,
  },
  whiteMarkLabel: {
    color: Colors.RemoGray05,
  },
  end: props => ({
    marginLeft: props.iconMargin ?? 16,
  }),
  start: props => ({
    marginRight: props.iconMargin ?? 16,
  }),
  icon: {
    flexShrink: 0,
    '& > svg': {
      display: 'block',
    },
  },
}));

interface ValueLabelComponentProps extends ValueLabelProps {
  valueLabelDisplay?: string;
}

function ValueLabelComponent(props: ValueLabelComponentProps) {
  const classes = useValueLabelStyles(props);
  const { children, className, open, value, valueLabelDisplay } = props;

  if (valueLabelDisplay === 'off') {
    return children;
  }

  let valueDisplay = value;
  if (typeof value === 'number') {
    valueDisplay = Math.round(value * 1000) / 1000;
  }

  return React.cloneElement(
    children,
    {
      className: clsx(
        children.props.className,
        {
          [classes.open]: open || valueLabelDisplay === 'on',
        },
        classes.thumb,
      ),
    },
    <span className={clsx(classes.offset, className)}>
      <span className={classes.circle}>
        <span className={classes.label}>{valueDisplay}</span>
      </span>
    </span>,
  );
}

// Copied from Material UI PrivateValueLabel component. Minor changes.
const useValueLabelStyles = makeStyles<ValueLabelComponentProps>(theme => ({
  thumb: {
    '&$open': {
      '& $offset': {
        transform: 'scale(1) translateY(-10px)',
      },
    },
  },
  open: {},
  offset: {
    zIndex: 1,
    ...theme.typography.body2,
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1.2,
    transition: theme.transitions.create(['transform'], {
      duration: theme.transitions.duration.shortest,
    }),
    top: -34,
    left: -6,
    transformOrigin: 'bottom center',
    transform: 'scale(0)',
    position: 'absolute',
  },
  circle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: '50% 50% 50% 0',
    backgroundColor: Colors.RemoGray50,
    transform: 'rotate(-45deg)',
  },
  label: {
    color: '#fff',
    background: Colors.RemoGray50,
    fontWeight: 500,
    lineHeight: '16px',
    fontSize: '11px',
    transform: 'rotate(45deg)',
    padding: '8px 12px',
    borderRadius: 4,
  },
}));

export default Slider;
